import Router from 'next/router';
import { startViewTransition, isViewTransitionSupported, prefersReducedMotion } from './browser/viewTransitions';

interface PushToRouterOptions {
  shallow?: boolean;
  locale?: string | false;
  scroll?: boolean;
  useViewTransition?: boolean;
}

interface PushToRouterParams {
  query: Record<string, any>;
  href: string;
  pathname?: string;
  options?: PushToRouterOptions;
}

/**
 * Navigates to the same page but with different query params
 * @see https://nextjs.org/docs#intercepting-popstate
 *
 * Required for client-side routing instead of full page refresh
 */
const pushToRouter = async ({
  query,
  href,
  pathname = Router.pathname,
  options = { shallow: true },
}: PushToRouterParams): Promise<void> => {
  if (!Router.isReady) return;

  const { useViewTransition = false, ...routerOptions } = options;

  const performNavigation = async () => {
    try {
      await Router.push({ pathname, query }, href, routerOptions);
    } catch (e: any) {
      if (!e.cancelled) {
        throw e;
      }
    }
  };

  if (useViewTransition && isViewTransitionSupported() && !prefersReducedMotion()) {
    await startViewTransition(performNavigation);
  } else {
    await performNavigation();
  }
};

export default pushToRouter;
